import { compressImage, fileExt, LONG_CACHE, PHOTO_MAX_SIDE } from "@/lib/image-compress";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Flag, Send, Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DEAL_TYPE_LABEL,
  DEAL_TYPE_TONE,
  LISTING_STATUS,
  LISTING_STATUS_LABEL,
  buildContactHref,
  contactPreferenceLabel,
  formatPrice,
  responseButtonLabel,
  timeAgo,
  type ListingStatus,
  type MarketplaceListing,
  type ListingResponse,
} from "@/lib/marketplace-constants";

export const Route = createFileRoute("/_authenticated/marketplace/$listingId")({
  component: ListingDetailPage,
});

type Author = { user_id: string; full_name: string; photo_url: string | null; city: string | null };

function ListingDetailPage() {
  const { listingId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activePhoto, setActivePhoto] = useState(0);
  const [showRespond, setShowRespond] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const listingQ = useQuery({
    queryKey: ["marketplace", "listing", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketplace_listings")
        .select("*")
        .eq("id", listingId)
        .maybeSingle();
      if (error) throw error;
      return data as MarketplaceListing | null;
    },
  });

  const listing = listingQ.data;
  const isOwner = !!user && !!listing && listing.user_id === user.id;

  const authorQ = useQuery({
    queryKey: ["marketplace", "author", listing?.user_id],
    enabled: !!listing?.user_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, city")
        .eq("user_id", listing!.user_id)
        .maybeSingle();
      if (error) throw error;
      return data as Author | null;
    },
  });

  const responsesQ = useQuery({
    queryKey: ["marketplace", "responses", listingId],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketplace_listing_responses")
        .select("*")
        .eq("listing_id", listingId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ListingResponse[];
    },
  });

  const setStatus = useMutation({
    mutationFn: async (status: ListingStatus) => {
      const { error } = await supabase
        .from("marketplace_listings")
        .update({ status })
        .eq("id", listingId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Статус обновлён");
      qc.invalidateQueries({ queryKey: ["marketplace"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (listingQ.isLoading) {
    return <div className="min-h-screen bg-white px-4 py-12 text-center text-td-muted">Загружаем…</div>;
  }
  if (!listing) {
    return (
      <div className="min-h-screen bg-white px-4 py-12 text-center">
        <p className="text-td-muted">Объявление не найдено.</p>
        <Link to="/marketplace" className="mt-4 inline-block text-td-teal underline">← К Барахолке</Link>
      </div>
    );
  }

  const author = authorQ.data;
  const cover = listing.photos?.[activePhoto] ?? listing.photos?.[0];

  const priceLine =
    listing.deal_type === "продам"
      ? formatPrice(listing.price, listing.currency) + (listing.price_negotiable ? " · торг" : "")
      : listing.deal_type === "отдам"
      ? "Бесплатно"
      : listing.deal_type === "ищу" && listing.budget != null
      ? `Бюджет: ${formatPrice(listing.budget, listing.currency)}`
      : null;

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-3xl px-5 py-8 md:py-12">
        <Link
          to="/marketplace"
          className="mb-4 inline-flex items-center gap-1 text-sm text-td-muted hover:text-td-teal"
        >
          <ArrowLeft className="h-4 w-4" /> К Барахолке
        </Link>

        {listing.photos && listing.photos.length > 0 ? (
          <div className="mb-4 overflow-hidden rounded-2xl border border-td-line bg-td-soft">
            <img loading="lazy" decoding="async"
              src={cover}
              alt={listing.title}
              className="aspect-[4/3] w-full object-cover"
            />
            {listing.photos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto p-2">
                {listing.photos.map((p, i) => (
                  <button
                    key={p}
                    onClick={() => setActivePhoto(i)}
                    className={`h-14 w-14 shrink-0 overflow-hidden rounded-md border ${
                      i === activePhoto ? "border-td-teal" : "border-td-line"
                    }`}
                  >
                    <img loading="lazy" decoding="async" src={p} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${DEAL_TYPE_TONE[listing.deal_type]}`}
          >
            {DEAL_TYPE_LABEL[listing.deal_type]}
          </span>
          <span className="text-[11px] font-medium uppercase tracking-wide text-td-muted">
            {listing.category}
          </span>
          {listing.status !== "active" && (
            <span className="rounded-full bg-td-soft px-2 py-0.5 text-[11px] font-semibold text-td-muted">
              {LISTING_STATUS_LABEL[listing.status]}
            </span>
          )}
        </div>

        <h1 className="font-display text-2xl font-bold text-td-dark md:text-3xl">
          {listing.title}
        </h1>

        {priceLine && (
          <div className="mt-2 text-xl font-bold text-td-dark">{priceLine}</div>
        )}

        <div className="mt-3 flex items-center gap-1 text-sm text-td-muted">
          <MapPin className="h-4 w-4" />
          {listing.city}{listing.district ? `, ${listing.district}` : ""}
          <span className="ml-2">· {timeAgo(listing.created_at)}</span>
        </div>

        {listing.description && (
          <div className="mt-4 whitespace-pre-wrap text-sm text-td-dark">
            {listing.description}
          </div>
        )}

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {listing.condition && (
            <div>
              <dt className="text-xs text-td-muted">Состояние</dt>
              <dd className="text-td-dark">{listing.condition}</dd>
            </div>
          )}
          {listing.delivery_available != null && (
            <div>
              <dt className="text-xs text-td-muted">Доставка</dt>
              <dd className="text-td-dark">{listing.delivery_available ? "Возможна" : "Нет"}</dd>
            </div>
          )}
          {listing.want_in_exchange && (
            <div className="col-span-2">
              <dt className="text-xs text-td-muted">Что хочу взамен</dt>
              <dd className="whitespace-pre-wrap text-td-dark">{listing.want_in_exchange}</dd>
            </div>
          )}
        </dl>

        {author && (
          <Link
            to="/people/$userId"
            params={{ userId: author.user_id }}
            className="mt-6 flex items-center gap-3 rounded-2xl border border-td-line p-3 hover:border-td-teal"
          >
            {author.photo_url ? (
              <img loading="lazy" decoding="async" src={author.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-td-soft font-semibold text-td-teal">
                {(author.full_name || "?").slice(0, 1)}
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate font-semibold text-td-dark">{author.full_name}</div>
              {author.city && (
                <div className="truncate text-xs text-td-muted">{author.city}</div>
              )}
            </div>
          </Link>
        )}

        {!isOwner && listing.status === "active" && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => (user ? setShowRespond(true) : navigate({ to: "/auth" }))}
              className="bg-td-teal text-white hover:bg-td-teal/90"
            >
              <Send className="mr-1 h-4 w-4" />
              {responseButtonLabel(listing.deal_type)}
            </Button>
            <Button variant="outline" onClick={() => setShowReport(true)}>
              <Flag className="mr-1 h-4 w-4" /> Пожаловаться
            </Button>
            {(() => {
              const href = buildContactHref(listing.contact_preference, listing.contact_value);
              if (!href) return null;
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-td-teal underline hover:no-underline"
                >
                  {contactPreferenceLabel(listing.contact_preference)}
                </a>
              );
            })()}
          </div>
        )}

        {isOwner && (
          <div className="mt-6 space-y-4">
            <div>
              <Label>Статус объявления</Label>
              <Select
                value={listing.status}
                onValueChange={(v) => setStatus.mutate(v as ListingStatus)}
              >
                <SelectTrigger className="mt-2 w-full sm:w-72"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LISTING_STATUS.map((s) => (
                    <SelectItem key={s} value={s}>{LISTING_STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <h2 className="mb-2 text-lg font-semibold text-td-dark">
                Отклики ({responsesQ.data?.length ?? 0})
              </h2>
              {responsesQ.data && responsesQ.data.length > 0 ? (
                <div className="space-y-2">
                  {responsesQ.data.map((r) => (
                    <ResponseRow key={r.id} response={r} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-td-muted">Пока никто не откликнулся.</p>
              )}
            </div>
          </div>
        )}
      </main>

      <RespondDialog
        open={showRespond}
        onOpenChange={setShowRespond}
        listingId={listing.id}
        dealType={listing.deal_type}
      />
      <ReportDialog
        open={showReport}
        onOpenChange={setShowReport}
        listingId={listing.id}
      />
    </div>
  );
}

function ResponseRow({ response }: { response: ListingResponse }) {
  const { data: sender } = useQuery({
    queryKey: ["profile", response.sender_user_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url")
        .eq("user_id", response.sender_user_id)
        .maybeSingle();
      return data as Author | null;
    },
  });
  return (
    <div className="rounded-xl border border-td-line p-3">
      <div className="flex items-center gap-2">
        {sender?.photo_url ? (
          <img loading="lazy" decoding="async" src={sender.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-td-soft" />
        )}
        <Link
          to="/people/$userId"
          params={{ userId: response.sender_user_id }}
          className="text-sm font-semibold text-td-dark hover:text-td-teal"
        >
          {sender?.full_name ?? "Пользователь"}
        </Link>
        <span className="ml-auto text-xs text-td-muted">{timeAgo(response.created_at)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-td-dark">{response.message}</p>
      {response.photo_url && (
        <img loading="lazy" decoding="async"
          src={response.photo_url}
          alt=""
          className="mt-2 max-h-48 rounded-lg border border-td-line object-cover"
        />
      )}
    </div>
  );
}

function RespondDialog({
  open,
  onOpenChange,
  listingId,
  dealType,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  listingId: string;
  dealType: MarketplaceListing["deal_type"];
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  async function upload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    setUploading(true);
    try {
      const upFile = await compressImage(file, { maxSide: PHOTO_MAX_SIDE });
      const ext = fileExt(upFile);
      const path = `${user.id}/responses/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from("marketplace")
        .upload(path, upFile, { contentType: upFile.type, cacheControl: LONG_CACHE });
      if (error) throw error;
      const { data } = supabase.storage.from("marketplace").getPublicUrl(path);
      setPhotoUrl(data.publicUrl);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Не удалось загрузить");
    } finally {
      setUploading(false);
    }
  }

  const send = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Unauthorized");
      if (!message.trim()) throw new Error("Напиши сообщение");
      const { error } = await supabase.from("marketplace_listing_responses").insert({
        listing_id: listingId,
        sender_user_id: user.id,
        message: message.trim(),
        photo_url: photoUrl,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Отклик отправлен");
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      setMessage("");
      setPhotoUrl(null);
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{responseButtonLabel(dealType)}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="Твоё сообщение автору"
          maxLength={1500}
        />
        <div>
          {photoUrl ? (
            <div className="relative inline-block">
              <img loading="lazy" decoding="async" src={photoUrl} alt="" className="h-24 w-24 rounded-lg object-cover" />
              <button
                onClick={() => setPhotoUrl(null)}
                className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-td-muted hover:text-td-teal">
              <input type="file" accept="image/*" className="sr-only" onChange={upload} disabled={uploading} />
              <Upload className="h-4 w-4" /> {uploading ? "Загружаем…" : "Прикрепить фото"}
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button
            onClick={() => send.mutate()}
            disabled={send.isPending}
            className="bg-td-teal text-white hover:bg-td-teal/90"
          >
            {send.isPending ? "Отправляем…" : "Отправить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({
  open,
  onOpenChange,
  listingId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  listingId: string;
}) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const report = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Unauthorized");
      const { error } = await supabase.from("marketplace_reports").insert({
        listing_id: listingId,
        reporter_user_id: user.id,
        reason: reason.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Жалоба отправлена, спасибо");
      setReason("");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Пожаловаться на объявление</DialogTitle>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="Опиши причину (необязательно)"
          maxLength={500}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button
            onClick={() => report.mutate()}
            disabled={report.isPending}
            className="bg-td-teal text-white hover:bg-td-teal/90"
          >
            Отправить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
